import { useEffect, useCallback, useState } from "react";
import { MapExperience } from "types/map";

type ReturnType = {
  savedTrees: number;
  savedAreaMeters: number;
  savedAreaKilometers: number;
  savedAreaSoccerFields: number;
  savedCO2: number;
  sliderValue: number;
  sliderMaxValue: number;
  onSliderChange: (newSliderValue: number) => void;
  logSliderInfo: (newSliderValue: number) => void;
};

export type ImpactYear = "2025" | "2026";

export type ImpactConstants = {
  maxSliderValue: number;
  treeMultiplier: number;
  currencyMultiplier: number;
  co2Multiplier: number;
  footballFieldArea: number;
};

export const IMPACT_CONFIG: Record<ImpactYear, ImpactConstants> = {
  "2025": {
    maxSliderValue: 1000000,
    treeMultiplier: 565,
    currencyMultiplier: 4.56,
    co2Multiplier: 550.5,
    footballFieldArea: 7140,
  },
  "2026": {
    maxSliderValue: 1000000,
    treeMultiplier: 565,
    currencyMultiplier: 1.93,
    co2Multiplier: 565,
    footballFieldArea: 7140,
  },
};

const getImpactYear = (experienceId: string | undefined): ImpactYear => {
  const isOld =
    experienceId &&
    (experienceId.includes("shed-norway-rainforest-partners-sx") ||
      experienceId.includes("shed-norway-rainforest-partners-help"));

  return isOld ? "2025" : "2026";
};

const squareMeterToHectar = (squareMeter: number) => squareMeter / 10000;
const squareMeterToKilometer = (squareMeter: number) => squareMeter / 1000000;
const squareMeterToSoccerField = (
  squareMeter: number,
  footballFieldArea: number
) => squareMeter / footballFieldArea;

const getSavedArea = (sliderValue: number, yearMetricsConfig: ImpactConstants) =>
  sliderValue * yearMetricsConfig.currencyMultiplier;

const getSavedTrees = (
  savedAreaMeters: number,
  yearMetricsConfig: ImpactConstants
) => squareMeterToHectar(savedAreaMeters) * yearMetricsConfig.treeMultiplier;

const getSavedCO2 = (
  savedAreaMeters: number,
  yearMetricsConfig: ImpactConstants
) => squareMeterToHectar(savedAreaMeters) * yearMetricsConfig.co2Multiplier;

const getSavedRadius = (savedAreaMeters: number) =>
  Math.round(Math.sqrt(savedAreaMeters / Math.PI));

type Props = {
  mapExperience: MapExperience;
};

const useMapImpactSlider = ({ mapExperience }: Props): ReturnType => {
  console.log("The map experience is:" + mapExperience.experienceId);

  const year = getImpactYear(mapExperience?.experienceId);
  const yearMetricsConfig = IMPACT_CONFIG[year];
  const { maxSliderValue, footballFieldArea } =
    yearMetricsConfig;

  const [sliderValue, setSliderValue] = useState(0);
  const [sliderMaxValue, setSliderMaxValue] = useState(maxSliderValue);

  useEffect(() => {
    const valueImpact = mapExperience?.valueImpact || 0;

    setSliderValue(valueImpact);
    setSliderMaxValue(Math.max(maxSliderValue, valueImpact));
  }, [mapExperience, maxSliderValue]);

  const savedAreaMeters = getSavedArea(sliderValue, yearMetricsConfig);
  const savedAreaKilometers = squareMeterToKilometer(savedAreaMeters);
  const savedAreaSoccerFields = squareMeterToSoccerField(
    savedAreaMeters,
    footballFieldArea
  );

  const savedTrees = getSavedTrees(savedAreaMeters, yearMetricsConfig);
  const savedCO2 = getSavedCO2(savedAreaMeters, yearMetricsConfig);

  const logSliderInfo = useCallback(
    (newSliderValue: number) => {
      const savedAreaMeters = getSavedArea(newSliderValue, yearMetricsConfig);
      const circleRadius = getSavedRadius(savedAreaMeters);

      console.log("[Slider] Saved area impact radius", circleRadius);
    },
    [yearMetricsConfig]
  );

  return {
    sliderValue,
    sliderMaxValue,
    onSliderChange: setSliderValue,
    logSliderInfo,
    savedTrees,
    savedAreaMeters,
    savedAreaKilometers,
    savedAreaSoccerFields,
    savedCO2,
  };
};

export default useMapImpactSlider;
